const CONTENT_ID = 'content';

/**
 * Extracts HTML from the current browser selection (within #content when present).
 */
export function getHTMLString(activeSelection: Selection | null | undefined): string {
	if (!activeSelection || activeSelection.rangeCount === 0) {
		return '';
	}
	return getHTMLWithinContent(activeSelection) ?? '';
}

/**
 * Returns selection HTML restricted to #content, or null.
 *
 * - Selection entirely inside #content → HTML for that selection.
 * - Selection crosses outside #content → HTML for only the part inside #content (clamped).
 * - Selection totally outside #content → null.
 * - No #content in DOM → original selection HTML (same as entirely within content).
 */
export function getHTMLWithinContent(selection: Selection): string | null {
	const contentEl = document.getElementById(CONTENT_ID);
	const selectionRange = selection.getRangeAt(0);

	if (!contentEl) {
		return rangeToHTML(selectionRange);
	}

	const rangeDoc = selectionRange.startContainer.ownerDocument;
	if (rangeDoc !== contentEl.ownerDocument) {
		return rangeToHTML(selectionRange);
	}

	const contentRange = document.createRange();
	contentRange.setStart(contentEl, 0);
	contentRange.setEnd(contentEl, contentEl.childNodes.length);

	const endToStart = selectionRange.compareBoundaryPoints(Range.END_TO_START, contentRange);
	const startToEnd = selectionRange.compareBoundaryPoints(Range.START_TO_END, contentRange);
	const entirelyBefore = endToStart <= 0 && startToEnd < 0;
	const entirelyAfter = startToEnd >= 0 && endToStart > 0;
	const doesNotIntersect = entirelyBefore || entirelyAfter;
	if (doesNotIntersect) {
		return null;
	}

	const rangeForStart =
		selectionRange.compareBoundaryPoints(Range.START_TO_START, contentRange) <= 0
			? contentRange
			: selectionRange;
	const rangeForEnd =
		selectionRange.compareBoundaryPoints(Range.END_TO_END, contentRange) >= 0
			? contentRange
			: selectionRange;

	const clampedRange = document.createRange();
	clampedRange.setStart(rangeForStart.startContainer, rangeForStart.startOffset);
	clampedRange.setEnd(rangeForEnd.endContainer, rangeForEnd.endOffset);
	return rangeToHTML(clampedRange);
}

/**
 * Converts a Range to its HTML representation (cloneContents → temp div → innerHTML).
 */
function rangeToHTML(range: Range): string {
	const fragment = range.cloneContents();
	const div = document.createElement('div');
	div.appendChild(fragment);
	return div.innerHTML;
}
